import Navbar from "@/Components/Navbar"
import { useNavigate, useNavigationType, useParams } from "react-router-dom"
import backgroundImage from "@/images/backgrounds/lobby-background.webp"
import { useCallback, useEffect, useMemo, useState } from "react"
import { getUserCreds, socket } from "@/utils/utils"
import Rank from "@/Components/Rank"
import rankImage from "@/images/rank.webp"

import MoneyAnimation from "@/Components/MoneyAnimation"
import { useAppDispatch, useAppSelector } from "@/store/hooks"
import { setModalData, setTempPropMngData } from "@/store/globalSlice"
import counterButtonBg from "@/images/trade-counter-btn.webp"
import acceptButtonBg from "@/images/trade-accept-btn.webp"
import declineButtonBg from "@/images/trade-decline-btn.webp"
import offerButtonBg from "@/images/trade-offer-btn.webp"
import dummyMiniMonopolyImage from "@/images/mini-monopoly-image.webp"
import ButtonWithBackground from "@/Components/ButtonWithBackground"
import Board from "../Game/Board"
import {
  getCellByOrderAndMapId,
  getCurrentPlayer,
  getPlayerByPlayerOrder,
  groupPropertiesByGroupId,
} from "@/utils/gameHelpers"
import mrMonopolyWaiting from "@/images/mr-monopoly/waiting.png"
import mrMonopolyOffering from "@/images/mr-monopoly/offering.png"
import mrMonopolyCreatingOffer from "@/images/mr-monopoly/creating-offer.png"
import mrMonopolySad from "@/images/mr-monopoly/sad.png"
import mrMonopolyAccepting from "@/images/mr-monopoly/accepting.png"
import { Audio, Blocks, Hourglass, ThreeDots } from "react-loader-spinner"
import { PropertyActionsCard } from "./PropertyActionsCard"
import { PrimaryButton } from "@/Components/PrimaryButton"
import Money from "@/Components/Money"
import classNames from "classnames"
import {
  getMoneyChangesAfterPropManagement,
  getNetMoneyChangeAfterPropManagment,
  getPropChangesAfterPropManagement,
} from "@/utils/propmngHelpers"

interface NewGameProps {}

const PropertyManagement: React.FC<NewGameProps> = () => {
  const params = useParams()
  const id = params.id
  const dispatch = useAppDispatch()
  const myGame = useAppSelector((state) => state.global.myGame)
  const tempPropMngData = useAppSelector(
    (state) => state.global.tempPropMngData
  )
  const propMngState = { myGame, tempPropMngData }
  const player = getCurrentPlayer(myGame as IGameDetails)
  const properties = player?.properties
    .map((propertyOrder) =>
      getCellByOrderAndMapId(
        propertyOrder,
        myGame?.settings.mapId as string
      )
    )
    .filter(Boolean) as ICell[]

  const nav = useNavigate()
  const groups = groupPropertiesByGroupId(properties)
  const moneyChange: number = getNetMoneyChangeAfterPropManagment(propMngState)
  const moneyChangeData = getMoneyChangesAfterPropManagement(propMngState)
  const handleApplyChanges = () => {
    socket.emit(
      "propmng.apply-changes",
      myGame!.id,
      getPropChangesAfterPropManagement(propMngState),
      moneyChange,
      tempPropMngData
    )

    dispatch(setModalData({ propMng: { visible: false } }))
  }
  useEffect(() => {
    dispatch(
      setTempPropMngData({
        mortgagedProperties: myGame?.situation?.mortgagedProperties,
        houseData: myGame?.situation?.housesData,
      })
    )
  }, [
    dispatch,
    myGame?.situation?.mortgagedProperties,
    myGame?.situation?.housesData,
  ])

  const handleCancelChanges = () => {
    dispatch(setModalData({ propMng: { visible: false } }))
  }
  return (
    <div
      data-testid="propmng"
      className="w-screen h-screen bg-cover flex flex-col px-28 pt-10 pb-10 fixed top-0 left-0 z-[100]"
      style={{ backgroundImage: `url(${backgroundImage})` }}
    >
      <div className="flex flex-col font-poppins gap-6 bg-trade-panelBackground border-trade-panelBorder px-8 py-8 overflow-auto ">
        <div className="text-center text-4xl font-semibold text-white">
          Manage your properties
        </div>
        <div className="text-white font-poppins">
          Click on the boxes to add or houses or hotels
        </div>
        <div className="grid grid-cols-[3fr_1fr] w-full overflow-auto">
          <div className="flex flex-col h-full gap-6 overflow-auto">
            {groups.map((group) => (
              <div className="flex flex-col gap-2">
                {group.properties!.map((prop) => (
                  <PropertyActionsCard
                    cell={prop}
                    showHouses
                    key={prop.order}
                  ></PropertyActionsCard>
                ))}
              </div>
            ))}
          </div>
          <div className="flex flex-col justify-end gap-4">
            <div className="flex justify-end">
              <div className="grid grid-cols-2 justify-items-end w-2/3 items-center">
                <div className="text-sm text-gray-300 ">Starting cash</div>
                <Money
                  money={player?.money || 0}
                  className="gap-2 text-base text-white"
                  iconClass="h-1/2"
                  testId="propmng.money-initial"
                ></Money>
                <div className="text-sm text-gray-300">Used</div>
                <Money
                  sign={moneyChange === 0 ? "" : moneyChange > 0 ? "+" : "-"}
                  money={Math.abs(moneyChange)}
                  className={classNames("gap-2 text-base ", {
                    "text-white": moneyChange === 0,
                    "text-green-400": moneyChange > 0,
                    "text-red-500": moneyChange < 0,
                  })}
                  iconClass="h-1/2"
                  testId="propmng.money-change"
                ></Money>
                <div className="text-sm text-gray-300">New cash total</div>
                <Money
                  money={player?.money + moneyChange}
                  className="gap-2 text-base text-white"
                  iconClass="h-1/2"
                  testId="propmng.money-final"
                />
              </div>
            </div>
            <div className="flex w-full justify-end gap-4">
              <PrimaryButton
                className="w-fit "
                data-testid="propmng.cancel-btn"
                onClick={handleCancelChanges}
              >
                <div className="text-lg font-poppins">Cancel</div>
              </PrimaryButton>
              <PrimaryButton
                onClick={handleApplyChanges}
                className="w-fit"
                data-testid="propmng.apply-btn"
              >
                <div className="text-lg font-poppins">Apply</div>
              </PrimaryButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PropertyManagement
